
import React, { useEffect, useState } from 'react';
import { User, UserProfileType } from '../types';
import { getAllUsers, updateUserRole, deleteUser, adminCreateUser } from '../services/authService';
import { 
  Trash2, Shield, ArrowLeft, Loader2, Search, RefreshCw, 
  Users as UsersIcon, ShieldOff, Copy, Check, UserPlus, 
  X, Mail, Key, Zap, ExternalLink, Terminal, Settings2, Link as LinkIcon 
} from 'lucide-react';
import { auth } from '../services/firebaseConfig';

interface Props {
  onBack: () => void;
}

const AdminPanel: React.FC<Props> = ({ onBack }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  
  const [newUserData, setNewUserData] = useState({
    email: '',
    name: '',
    password: '',
    profile: UserProfileType.FREE
  });
  const [creatingUser, setCreatingUser] = useState(false);

  const currentUserUid = auth.currentUser?.uid;
  const PROBABLE_WEBHOOK_URL = "https://us-central1-cromoterapia-aplicada.cloudfunctions.net/stripeWebhook";

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const allUsers = await getAllUsers();
      setUsers(allUsers.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (err: any) {
      setError(err.code === 'permission-denied' ? 'PERMISSAO_NEGADA' : 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(PROBABLE_WEBHOOK_URL);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    setUpdatingId(userId);
    try {
      await updateUserRole(userId, newRole as UserProfileType);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, profile: newRole as UserProfileType } : u));
    } finally {
      setUpdatingId(null);
    }
  };

  const getProfileColor = (role: UserProfileType) => {
    switch(role) {
      case UserProfileType.MASTER: return 'bg-red-500 text-white';
      case UserProfileType.OURO: return 'bg-yellow-500 text-black';
      case UserProfileType.PRATA: return 'bg-slate-300 text-black';
      case UserProfileType.BRONZE: return 'bg-orange-500 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  return (
    <div className="space-y-8 animate-fade-in pb-20 text-white">
      {/* CABEÇALHO */}
      <div className="flex justify-between items-center bg-purple-900/50 p-4 rounded-2xl border border-purple-500/30">
        <button onClick={onBack} className="flex items-center gap-2 font-black uppercase text-xs tracking-widest hover:text-chroma-accent transition-colors">
          <ArrowLeft size={20} /> Voltar
        </button>
        <div className="flex items-center gap-4">
           <button onClick={() => setShowAddModal(true)} className="bg-white text-black px-4 py-2 rounded-xl font-black text-xs uppercase flex items-center gap-2 hover:bg-chroma-accent hover:text-white transition-all">
              <UserPlus size={16} /> Novo Aluno
           </button>
           <div className="bg-red-600 px-3 py-1.5 rounded-lg flex items-center gap-2">
              <Shield size={14} />
              <span className="text-[10px] font-black uppercase">Mestre</span>
           </div>
        </div>
      </div>

      {/* BOX DE CONFIGURAÇÃO - ALTO CONTRASTE (AMARELO) */}
      <div className="bg-yellow-400 p-8 rounded-[2rem] shadow-2xl text-black">
        <div className="flex flex-col md:flex-row gap-8 items-center">
          <div className="bg-black text-yellow-400 p-5 rounded-2xl shadow-xl animate-bounce">
            <Zap size={40} fill="currentColor" />
          </div>
          <div className="flex-1 space-y-4 text-center md:text-left">
            <h2 className="text-3xl font-black uppercase tracking-tighter">Ativar Automação Stripe</h2>
            <p className="font-bold text-sm opacity-80 leading-tight">
              Copie o link abaixo e cole no Stripe (Webhooks) para liberação automática.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <div className="flex-1 bg-black/10 border-2 border-black/20 rounded-xl p-3 flex items-center justify-between gap-4 overflow-hidden">
                <code className="text-xs font-mono font-black truncate">{PROBABLE_WEBHOOK_URL}</code>
                <button 
                  onClick={handleCopyUrl}
                  className="bg-black text-white px-4 py-2 rounded-lg text-[10px] font-black uppercase hover:bg-gray-800 shrink-0 flex items-center gap-2"
                >
                  {copiedUrl ? <Check size={14} /> : <Copy size={14} />}
                  {copiedUrl ? "Copiado" : "Copiar Link"}
                </button>
              </div>
              
              <a 
                href="https://dashboard.stripe.com/webhooks" 
                target="_blank" 
                className="bg-black text-white px-6 py-4 rounded-xl text-xs font-black uppercase flex items-center justify-center gap-2 hover:scale-105 transition-transform"
              >
                Abrir Stripe <ExternalLink size={16} />
              </a>
            </div>
          </div>
        </div>

        {/* COMANDOS RÁPIDOS */}
        <div className="mt-8 pt-6 border-t border-black/10 grid grid-cols-1 md:grid-cols-2 gap-4">
           <div className="bg-black/5 p-4 rounded-xl space-y-2 border border-black/10">
              <p className="text-[10px] font-black uppercase">1. Chaves no Terminal:</p>
              <code className="block text-[9px] font-mono break-all font-bold">firebase functions:config:set stripe.webhook_secret="whsec_..."</code>
           </div>
           <div className="bg-black/5 p-4 rounded-xl space-y-2 border border-black/10">
              <p className="text-[10px] font-black uppercase">2. Finalizar:</p>
              <code className="block text-[9px] font-mono font-bold">firebase deploy --only functions</code>
           </div>
        </div>
      </div>

      {/* LISTA DE USUÁRIOS */}
      <div className="bg-chroma-card border border-purple-800/50 rounded-[2rem] overflow-hidden shadow-xl">
        <div className="p-6 border-b border-purple-800/50 flex flex-col sm:flex-row gap-4 items-center justify-between">
           <div className="flex items-center gap-3">
              <UsersIcon className="text-purple-400" />
              <h3 className="font-bold text-xl">Base de Alunos ({users.length})</h3>
           </div>
           <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-2.5 text-purple-500" size={16} />
              <input 
                type="text" 
                placeholder="Filtrar alunos..." 
                className="w-full bg-black/40 border border-purple-800 rounded-xl py-2 pl-10 pr-4 text-sm outline-none focus:border-chroma-accent"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
           </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-20 flex justify-center"><Loader2 className="animate-spin text-chroma-accent" size={40} /></div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-purple-900/50 text-purple-300 font-black uppercase tracking-widest text-[9px]">
                <tr>
                  <th className="px-6 py-4">Aluno</th>
                  <th className="px-6 py-4">Plano Atual</th>
                  <th className="px-6 py-4 text-center">Uso Hoje</th>
                  <th className="px-6 py-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-purple-800/30">
                {users.filter(u => u.name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase())).map(u => (
                  <tr key={u.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-bold text-sm">{u.name}</div>
                      <div className="text-purple-400 opacity-60">{u.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <select 
                        value={u.profile}
                        disabled={u.id === currentUserUid || updatingId === u.id}
                        onChange={e => handleRoleChange(u.id, e.target.value)}
                        className={`px-3 py-1.5 rounded-lg font-black uppercase text-[10px] outline-none border-none cursor-pointer ${getProfileColor(u.profile)}`}
                      >
                        {Object.values(UserProfileType).map(role => <option key={role} value={role}>{role}</option>)}
                      </select>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="bg-purple-900/50 px-3 py-1 rounded-full font-bold">{u.requestsToday}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {u.id !== currentUserUid && (
                        <button onClick={() => { if(confirm('Excluir?')) deleteUser(u.id).then(fetchUsers)}} className="text-gray-500 hover:text-red-500 p-2"><Trash2 size={16} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* MODAL NOVO ALUNO */}
      {showAddModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md bg-[#1a082e] border border-purple-500/30 rounded-[2.5rem] shadow-2xl p-8">
            <div className="flex justify-between items-center mb-8">
               <h3 className="text-xl font-black uppercase tracking-widest text-emerald-500">Novo Cadastro Manual</h3>
               <button onClick={() => setShowAddModal(false)} className="text-purple-400"><X size={24}/></button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setCreatingUser(true);
              try {
                await adminCreateUser(newUserData.email, newUserData.password, newUserData.name, newUserData.profile);
                setShowAddModal(false);
                fetchUsers();
              } finally { setCreatingUser(false); }
            }} className="space-y-4">
              <input required placeholder="Nome Completo" className="w-full bg-black border border-purple-800 rounded-xl p-4 text-sm" value={newUserData.name} onChange={e => setNewUserData({...newUserData, name: e.target.value})} />
              <input required type="email" placeholder="E-mail" className="w-full bg-black border border-purple-800 rounded-xl p-4 text-sm" value={newUserData.email} onChange={e => setNewUserData({...newUserData, email: e.target.value})} />
              <input required placeholder="Senha (min 6)" className="w-full bg-black border border-purple-800 rounded-xl p-4 text-sm" value={newUserData.password} onChange={e => setNewUserData({...newUserData, password: e.target.value})} />
              <select className="w-full bg-black border border-purple-800 rounded-xl p-4 text-sm" value={newUserData.profile} onChange={e => setNewUserData({...newUserData, profile: e.target.value as UserProfileType})}>
                {Object.values(UserProfileType).map(role => <option key={role} value={role}>{role}</option>)}
              </select>
              <button type="submit" disabled={creatingUser} className="w-full bg-emerald-600 text-white font-black py-4 rounded-xl uppercase tracking-widest shadow-xl">
                {creatingUser ? "Processando..." : "Cadastrar Agora"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
